import React, { useCallback, useMemo } from 'react'
import { Cascader } from 'antd'
import { useSelector } from 'react-redux'
import { useQueries } from '@tanstack/react-query'
import { DefaultOptionType } from 'antd/es/cascader'
import { NodeContentProps, NodesById, NormalizedBuilderNode } from '../../types'
import { customReportGrammarSelector } from '../../../../../../../../redux/config/customReport/selectors'
import { ReferenceablePathsVisitor } from '../../normalizedVisitors/ReferenceablePathsVisitor'
import { NormalizedExportVisitor } from '../../normalizedVisitors/NormalizedExportVisitor'
import { getDimensionOptions } from '../../../../../../../../features/dimension/queries/getDimensionOptions'
import { getDimensionTree } from '../../../../../../../../features/dimension/components/utils'
import { currentUserSortedCompaniesSelector } from '../../../../../../../../redux/session/currentUser/selectors'
import { getAllCompanyIds } from './utils/companyUtils'
import { getNodeLabel, buildCascaderTree, findCurrentValuePath } from './utils/referenceUtils'
import { Dimension } from '../../../../../../../../features/dimension/types/Dimension'

// Laajennettu props-tyyppi normalisoitua dataa varten
interface ExtendedNodeContentProps extends NodeContentProps {
  nodesById: NodesById
  rootId: string // Juurisolmun ID
}

const ReferenceNodeContent: React.FC<ExtendedNodeContentProps> = ({
  node,
  onUpdateValue,
  nodesById,
  rootId,
  context
}) => {
  const rules = useSelector(customReportGrammarSelector)

  const companyIds = useMemo(() => getAllCompanyIds(nodesById), [nodesById])
  const primaryCompanyId = context?.company || companyIds[0]

  // Memoize exportVisitor to prevent unnecessary recreations
  const exportVisitor = useMemo(() => new NormalizedExportVisitor(rules, nodesById), [rules, nodesById])

  // Dynamically fetch dimension trees for all companies using useQueries
  const dimensionQueries = useQueries({
    queries: companyIds.map(companyId => getDimensionOptions(companyId, getDimensionTree))
  })

  // Create dimension trees mapping from query results - memoized to prevent unnecessary re-renders
  const dimensionTrees = useMemo(() => {
    const trees: { [companyId: string]: Dimension[] | undefined } = {}
    dimensionQueries.forEach((query, index) => {
      const companyId = companyIds[index]
      if (companyId && query?.data) {
        trees[companyId] = query.data
      }
    })
    return trees
  }, [dimensionQueries, companyIds])

  // Get user companies for company name lookup
  const companies = useSelector(currentUserSortedCompaniesSelector)

  // Memoizoi category tree creation
  const categoryTree = useMemo(() => {
    try {
      const tree = exportVisitor.exportChildren(rootId)
      return tree
    } catch (exportError) {
      console.warn('Failed to export category tree:', exportError)
      return []
    }
  }, [exportVisitor, rootId])

  // Memoizoi visitor-instanssi
  const visitor = useMemo(() => new ReferenceablePathsVisitor(rules, nodesById, rootId), [rules, nodesById, rootId])

  // Memoizoi polkujen haku
  const paths = useMemo(() => {
    try {
      return visitor.findReferenceablePaths(node.id)
    } catch (pathError) {
      console.error('Failed to find referenceable paths:', pathError)
      return []
    }
  }, [visitor, node.id])

  // Memoize dependencies for getNodeLabel
  const labelDeps = useMemo(
    () => ({ nodesById, dimensionTrees, companies, primaryCompanyId, categoryTree }),
    [nodesById, dimensionTrees, companies, primaryCompanyId, categoryTree]
  )

  // Memoized label getter
  const getLabelForNode = useCallback((n: NormalizedBuilderNode) => getNodeLabel(n, labelDeps), [labelDeps])

  // Memoizoi options-array with dimension trees dependency
  const options = useMemo(
    () => buildCascaderTree(paths, nodesById, getLabelForNode),
    [paths, nodesById, getLabelForNode]
  )

  // Memoizoi onChange-callback - Cascader returns array of values, we need the last one
  // Only allow selection of referenceable nodes
  const onChange = useCallback(
    (value: (string | number | null)[], selectedOptions: DefaultOptionType[]) => {
      if (value && value.length > 0 && selectedOptions && selectedOptions.length > 0) {
        const lastOption = selectedOptions[selectedOptions.length - 1]
        // Only accept if it's a referenceable node
        if (lastOption.isReferenceable) {
          const lastValue = value[value.length - 1]
          if (lastValue !== null) {
            onUpdateValue(node.id, { value: lastValue as string })
          }
        }
      }
    },
    [node.id, onUpdateValue]
  )

  // Find the current value's full path for Cascader
  const currentValuePath = useMemo(() => findCurrentValuePath(node.value, paths), [node.value, paths])

  // Custom display renderer to show labels instead of values
  const displayRender = useCallback((labels: string[], selectedOptions?: DefaultOptionType[]) => {
    // Prefer labels from selectedOptions if available
    if (selectedOptions && selectedOptions.length > 0) {
      return selectedOptions.map(opt => opt.label as string).join(' / ')
    }
    return labels.join(' / ')
  }, [])

  // Custom option renderer to style non-referenceable nodes
  const optionRender = useCallback(
    (option: DefaultOptionType) => (
      <span style={{ color: option.isReferenceable ? undefined : '#999' }}>{option.label as string}</span>
    ),
    []
  )

  return (
    <Cascader
      style={{ width: 500 }}
      showSearch
      options={options}
      onChange={onChange}
      value={currentValuePath}
      changeOnSelect
      expandTrigger="hover"
      displayRender={displayRender}
      optionRender={optionRender}
    />
  )
}

export default ReferenceNodeContent
